import {
    INDICATOR_TEMPLATE_STATUS,
    IndicatorTemplateStatusT,
} from '../../types';
import React, { useMemo } from 'react';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { getAvailableFunctions, getAvailableSoftwares } from '../helpers';

import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import { ManageChartTemplateActionType } from '../../ManageChartTemplateContext/ManageChartTemplateReducer';
import MenuItem from '@mui/material/MenuItem';
import { SELECT_ALL_OPTION } from '../../ManageChartTemplateContext/helpers';
import TextField from '@mui/material/TextField';
import { snakify } from 'helpers/syntaxHelper';
import { styled } from '@mui/material';
import { useGetAllSoftwares } from '../hooks/getAllSoftwares';
import { useManageChartTemplateContext } from '../../ManageChartTemplateContext/ManageChartTemplateContext';
import { useTranslation } from 'react-i18next';

const DETAILS_FORM_PREFIX =
    'support.chartsTemplates.createOrEditModal.detailsForm';

const StyTextField = styled(TextField)(({ theme }) => ({
    '& .MuiInputBase-root': {
        backgroundColor: theme.palette.background.paper,
    },
}));

const StyFormControl = styled(FormControl, {
    shouldForwardProp: (prop) => prop !== 'isStatus',
})<{ isStatus?: boolean }>(({ isStatus = false, theme }) => ({
    background: theme.palette.background.paper,
    ...(isStatus ? { width: '250px' } : {}),
}));

const StyGeneralInfoFormContainer = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
}));

const StyTitleAndStatusContainer = styled('div')(({ theme }) => ({
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(2),
    justifyContent: 'space-between',
}));

const GeneralInfoForm = () => {
    const { t } = useTranslation();

    const {
        state: {
            authorizedFunctions,
            authorizedSoftwares,
            description,
            status,
            title,
        },
        dispatch,
    } = useManageChartTemplateContext();

    const { data: allSoftwares } = useGetAllSoftwares();
    const availableFunctions = useMemo(
        () => getAvailableFunctions(allSoftwares, t),
        [allSoftwares],
    );
    const availableSoftwares = useMemo(
        () => getAvailableSoftwares(allSoftwares, authorizedFunctions),
        [allSoftwares, authorizedFunctions],
    );
    if (!allSoftwares) return <></>;

    const handleStatusChange = (event: SelectChangeEvent) => {
        dispatch({
            type: ManageChartTemplateActionType.CustomizeChartTemplate,
            payload: { status: event.target.value as IndicatorTemplateStatusT },
        });
    };
    const handleAuthorizedFunctionsChange = (
        event: SelectChangeEvent<string[]>,
    ) => {
        const { value } = event.target;
        if (typeof value === 'string') return;
        dispatch({
            type: ManageChartTemplateActionType.UpdateAuthorizedFunctions,
            // we need to sort the new authorized functions to keep a similar order for the available softwares list
            newAuthorizedFunctions: value.sort((a, b) =>
                new Intl.Collator().compare(
                    t(`cellar.${snakify(a)}`),
                    t(`cellar.${snakify(b)}`),
                ),
            ),
        });
    };
    const handleAuthorizedSoftwaresChange = (
        event: SelectChangeEvent<string[]>,
    ) => {
        const { value } = event.target;
        if (typeof value === 'string') return;
        dispatch({
            type: ManageChartTemplateActionType.UpdateAuthorizedSoftwares,
            newAuthorizedSoftwares: value,
        });
    };

    return (
        <StyGeneralInfoFormContainer>
            <StyTitleAndStatusContainer>
                <StyTextField
                    fullWidth
                    required
                    label={t(`${DETAILS_FORM_PREFIX}.title`)}
                    value={title}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        dispatch({
                            type: ManageChartTemplateActionType.CustomizeChartTemplate,
                            payload: { title: event.target.value },
                        });
                    }}
                    variant="outlined"
                />
                <StyFormControl isStatus>
                    <InputLabel>
                        {t('support.chartsTemplates.gridSummary.status.title')}
                    </InputLabel>
                    <Select
                        value={status}
                        label={t(
                            'support.chartsTemplates.gridSummary.status.title',
                        )}
                        onChange={handleStatusChange}
                    >
                        {Object.values(INDICATOR_TEMPLATE_STATUS).map(
                            (statusOption) => (
                                <MenuItem
                                    key={statusOption}
                                    value={statusOption}
                                >
                                    {t(
                                        `support.chartsTemplates.gridSummary.status.${statusOption}`,
                                    )}
                                </MenuItem>
                            ),
                        )}
                    </Select>
                </StyFormControl>
            </StyTitleAndStatusContainer>
            <StyTextField
                fullWidth
                label={t(`${DETAILS_FORM_PREFIX}.description`)}
                value={description}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    dispatch({
                        type: ManageChartTemplateActionType.CustomizeChartTemplate,
                        payload: { description: event.target.value },
                    });
                }}
                variant="outlined"
            />
            <StyFormControl>
                <InputLabel>
                    {t(`${DETAILS_FORM_PREFIX}.authorizedFunctions`)}
                </InputLabel>
                <Select
                    multiple
                    value={authorizedFunctions}
                    label={t(`${DETAILS_FORM_PREFIX}.authorizedFunctions`)}
                    onChange={handleAuthorizedFunctionsChange}
                >
                    {availableFunctions.map((func) => (
                        <MenuItem key={func.id} value={func.id}>
                            {func.name}
                        </MenuItem>
                    ))}
                </Select>
            </StyFormControl>
            <StyFormControl>
                <InputLabel>
                    {t(`${DETAILS_FORM_PREFIX}.authorizedSoftwares`)}
                </InputLabel>
                <Select
                    multiple
                    value={authorizedSoftwares}
                    label={t(`${DETAILS_FORM_PREFIX}.authorizedSoftwares`)}
                    onChange={handleAuthorizedSoftwaresChange}
                >
                    {authorizedFunctions.length > 0 && (
                        <MenuItem
                            divider
                            key="select-all"
                            value={SELECT_ALL_OPTION}
                        >
                            {t(`${DETAILS_FORM_PREFIX}.selectAllOption`)}
                        </MenuItem>
                    )}
                    {availableSoftwares.map((software) => (
                        <MenuItem key={software} value={software}>
                            {software}
                        </MenuItem>
                    ))}
                </Select>
            </StyFormControl>
        </StyGeneralInfoFormContainer>
    );
};

export default GeneralInfoForm;
